import type { StatusTone } from '../../abf/components/StatusChip';
import type { ReviewRow } from '../../abf/onboarding/review/reviewModel';
import type { AgreementReviewRecord, SourceCitation } from './agreementReviewApi';

/** One reviewable line of the document: a term, a change, or a covenant with its tests. */
export interface AgreementRow {
  uid: string;
  section: 'terms' | 'covenants';
  group: string;
  label: string;
  value: string;
  detail: string | null;
  /** What extraction first read, when a reviewer corrected the value. */
  extractedValue: string | null;
  citation: SourceCitation;
  /** The value a reviewer's citation for this row is recorded against. */
  sourcePointer: string;
}

export interface AgreementGroup {
  name: string;
  rows: AgreementRow[];
  /** Terms the document does not state, named so their absence is visible. */
  notStated: string[];
}

/** What kind of document is under review; it decides what the page leads with. */
export type DocumentKind = 'AGREEMENT' | 'AMENDMENT' | 'NOTICE' | 'CERTIFICATE' | 'TRADE_CONFIRM';
/** Private credit is a direct loan the fund originates; a leveraged loan is a syndicated deal bought in the secondary market. */
export type AssetLane = 'PRIVATE_CREDIT' | 'LEVERAGED_LOAN';

const LEVERAGED_LOAN_TYPES = new Set([
  'SYNDICATED_TERM_SHEET', 'AGENT_BANK_NOTICE', 'LSTA_TRADE_CONFIRM', 'LSTA_DRAWDOWN_NOTICE',
  'LSTA_PAYDOWN_NOTICE', 'LSTA_RATE_RESET', 'LSTA_AMENDMENT',
]);
/** Every document type that has a term-by-term review record. */
export const REVIEWABLE_DOCUMENT_TYPES = new Set([
  'CREDIT_AGREEMENT', 'AMENDMENT', 'COMPLIANCE_CERTIFICATE', 'LOAN_NOTICE', 'DRAWDOWN_NOTICE',
  'PAYDOWN_NOTICE', 'RATE_RESET_NOTICE', ...Array.from(LEVERAGED_LOAN_TYPES),
]);

export const assetLane = (documentType: string | null | undefined): AssetLane =>
  LEVERAGED_LOAN_TYPES.has(String(documentType || '').toUpperCase()) ? 'LEVERAGED_LOAN' : 'PRIVATE_CREDIT';

export const documentKind = (documentType: string | null | undefined): DocumentKind => {
  const type = String(documentType || '').toUpperCase();
  if (type === 'CREDIT_AGREEMENT' || type === 'SYNDICATED_TERM_SHEET') return 'AGREEMENT';
  if (type === 'LSTA_TRADE_CONFIRM') return 'TRADE_CONFIRM';
  if (type.includes('AMENDMENT')) return 'AMENDMENT';
  if (type.includes('CERTIFICATE')) return 'CERTIFICATE';
  return 'NOTICE';
};

const DOCUMENT_WORDS: Record<string, string> = {
  CREDIT_AGREEMENT: 'agreement', SYNDICATED_TERM_SHEET: 'agreement',
  AMENDMENT: 'amendment', LSTA_AMENDMENT: 'amendment',
  COMPLIANCE_CERTIFICATE: 'compliance certificate',
  DRAWDOWN_NOTICE: 'drawdown notice', LSTA_DRAWDOWN_NOTICE: 'drawdown notice',
  PAYDOWN_NOTICE: 'paydown notice', LSTA_PAYDOWN_NOTICE: 'paydown notice',
  RATE_RESET_NOTICE: 'rate reset notice', LSTA_RATE_RESET: 'rate reset notice',
  LOAN_NOTICE: 'loan notice', AGENT_BANK_NOTICE: 'agent bank notice',
  LSTA_TRADE_CONFIRM: 'trade confirmation',
};
const EVENT_WORDS: Record<string, string> = {
  DRAWDOWN: 'drawdown notice', PAYDOWN: 'paydown notice', PREPAYMENT: 'paydown notice',
  INTEREST_PAYMENT: 'interest notice', RATE_RESET: 'rate reset notice',
};
/** The document in the operator's words: "drawdown notice". A general loan notice is named by its event. */
export const documentWord = (documentType: string | null | undefined, eventType?: string | null): string => {
  const type = String(documentType || '').toUpperCase();
  if ((type === 'LOAN_NOTICE' || type === 'AGENT_BANK_NOTICE') && eventType && EVENT_WORDS[String(eventType).toUpperCase()]) {
    return EVENT_WORDS[String(eventType).toUpperCase()];
  }
  return DOCUMENT_WORDS[type] || 'document';
};

const TERMS_GROUP = 'Commitment and dates';
const LENDER_GROUP = 'Lenders';
/** The record's top-level blocks, in the order a credit agreement is read. */
const GROUPS: { name: string; keys: string[] }[] = [
  { name: 'Trade', keys: ['trade_settlement_details', 'trade_confirmation'] },
  { name: 'Notice', keys: ['notice_event'] },
  { name: 'Interest payment', keys: ['interest_payment_inputs'] },
  { name: 'Amendment', keys: ['amendment_name', 'amendment_pricing', 'amendment_updates'] },
  { name: 'Facility', keys: ['deal_name', 'deal_code', 'loan_identifiers', 'governing_law', 'cov_lite_acknowledged'] },
  { name: 'Borrower and sponsor', keys: ['borrower', 'sponsor_info'] },
  { name: TERMS_GROUP, keys: ['loan_terms', 'key_dates'] },
  { name: 'Pricing and fees', keys: ['interest_rate'] },
  { name: 'Payments', keys: ['payment_terms', 'pik_toggle'] },
  { name: LENDER_GROUP, keys: ['lender_register'] },
];
/** What a notice, amendment or trade is about comes first; an agreement leads with the facility. */
const EVENT_GROUPS = new Set(['Trade', 'Notice', 'Interest payment', 'Amendment']);
const OTHER_GROUP = 'Other terms';
const COVENANT_GROUP = 'Financial covenants';
/** Bookkeeping and commentary the extraction adds; not a term of the document. */
const NOT_TERMS = new Set([
  'field_evidence', 'documented_loanx_count', 'confidence', 'rationale',
  'page_reference', 'evidence_text', 'source_page', 'pdf_page', 'source_section', 'source_clause', 'source_excerpt', 'source_lineage',
]);

/**
 * Plain names for the extracted fields. The order here is also the order they are shown in:
 * the stored record does not keep the document's own order.
 */
const LABELS: Record<string, string> = {
  event_type: 'Event', event_amount: 'Amount', effective_date: 'Effective date', notice_date: 'Notice date',
  trade_type: 'Trade type', assignor: 'Seller (assignor)', assignee: 'Buyer (assignee)',
  par_amount: 'Par amount', transfer_price: 'Price', trade_date: 'Trade date', settlement_date: 'Settlement date',
  principal_consideration: 'Principal consideration', accrued_interest: 'Accrued interest', accrued_days: 'Accrued days',
  assignment_fee: 'Assignment fee', total_settlement_amount: 'Total settlement',
  lsta_standard_terms: 'LSTA standard terms', confirmation_reference: 'Confirmation reference',
  trade_confirmation_id: 'Confirmation reference',
  amendment_name: 'Amendment', pricing_effective_date: 'Pricing effective date',
  deal_name: 'Deal name', deal_code: 'Deal code',
  tranche_code: 'Tranche', facility_name: 'Facility name', facility_type: 'Facility type', loan_id: 'Loan ID',
  cusip: 'CUSIP', isin: 'ISIN', lsta_id: 'LSTA ID', loanx_id: 'LoanX ID', wso_loan_id: 'WSO loan ID',
  original_size: 'Size', governing_law: 'Governing law', cov_lite_acknowledged: 'Covenant-lite',
  legal_name: 'Borrower', lei_code: 'LEI', jurisdiction: 'Jurisdiction of organization',
  industry_sector: 'Sector', sub_industry: 'Sub-industry',
  credit_rating: 'Credit rating', sp_rating: 'S&P rating', moodys_rating: "Moody's rating", fitch_rating: 'Fitch rating',
  sponsor_name: 'Sponsor', sponsor_type: 'Sponsor type', fund_name: 'Sponsor fund',
  original_principal: 'Commitment', total_commitment: 'Total commitment', current_principal: 'Current principal',
  drawdown_amount: 'Drawdown amount', previously_funded: 'Funded before this draw',
  new_funded_amount: 'Funded after this draw', remaining_unfunded: 'Unfunded after this draw',
  currency: 'Currency',
  document_date: 'Document date', notice_effective_date: 'Effective date',
  origination_date: 'Closing date', closing_date: 'Closing date', maturity_date: 'Maturity date',
  availability_end_date: 'Availability ends',
  loan_status: 'Status', settlement_convention: 'Settlement convention',
  base_type: 'Index', base_rate: 'Index', base_rate_pct: 'Index rate', spread_bps: 'Spread',
  csa_bps: 'Credit spread adjustment', floor_pct: 'Rate floor', floor_rate: 'Rate floor', floor_basis: 'Floor applies to',
  all_in_rate_pct: 'All-in rate',
  step_down_spread_bps: 'Step-down spread', step_down_trigger_leverage: 'Step-down trigger',
  commitment_fee_bps: 'Commitment fee', ticking_fee_bps: 'Ticking fee', upfront_fee_bps: 'Upfront fee', oid_price: 'OID price',
  payment_type: 'Payment type', payment_frequency: 'Frequency', day_count_convention: 'Day count',
  next_payment_date: 'Next payment date', next_payment_amount: 'Next payment amount',
  outstanding_principal: 'Outstanding principal', interest_period_start: 'Interest period start',
  interest_period_end: 'Interest period end', days_in_period: 'Days in period',
  stated_interest_amount: 'Interest stated in the notice',
  has_pik_option: 'PIK option', current_election: 'Current election',
  cash_percentage: 'Cash share', pik_percentage: 'PIK share', pik_premium_bps: 'PIK premium',
};
const ORDER = Object.keys(LABELS);
const rank = (key: string): number => { const at = ORDER.indexOf(key); return at < 0 ? ORDER.length : at; };
/** Fields that belong to notices and certificates: shown when stated, never listed as missing. */
const ONLY_WHEN_STATED = /^(notice_|interest_period_|compliance_period_|amendment_|interest_payment_inputs$|trade_)/;
/** Abbreviations that stay in capitals when a code is written out as words. */
const ABBREVIATIONS = new Set(['SOFR', 'PE', 'PIK', 'LIBOR', 'EURIBOR', 'SONIA', 'LTM', 'OIS', 'LLC', 'LP', 'EBITDA', 'LSTA', 'DDTL', 'RCF', 'TLA', 'TLB']);
const RECORD_NAMES: Record<string, string> = {
  lender_register: 'Lender', amendment_updates: 'Change', step_downs: 'Step-down',
};

const MONEY_KEYS = /(^|_)(principal|amount|commitment|cap|balance|proceeds|funded|unfunded|debt|drawdown|size|consideration|interest|fee)($|_)/;
/** Bank details are shown as the last four characters only. */
const BANK_DETAIL = /(account_number|routing|iban|swift|aba)/;
const NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 });
const RATIO = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const RATE = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const sentence = (key: string): string => {
  const words = key.replace(/_(bps|pct|percentage)$/, '').split('_')
    .map(word => (ABBREVIATIONS.has(word.toUpperCase()) ? word.toUpperCase() : word)).join(' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
const labelOf = (key: string): string => LABELS[key] || sentence(key);

const formatDate = (raw: string): string => {
  const date = new Date(`${raw.slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? raw
    : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

/** A stored code such as TERM_SOFR or QUARTERLY as words; identifiers and prose are left as written. */
const codeWords = (text: string): string => {
  if (!/^[A-Z]{4,}$|^[A-Z]+(_[A-Z]+)+$/.test(text)) return text;
  const words = text.split('_').map(word => (ABBREVIATIONS.has(word) ? word : word.toLowerCase())).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const formatValue = (key: string, value: unknown, currency: string | null): string => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (BANK_DETAIL.test(key)) return `•••• ${String(value).slice(-4)}`;
  if (typeof value === 'number') {
    if (/_bps$/.test(key)) return `${NUMBER.format(value)} bps`;
    if (/_(pct|percentage)$/.test(key) || key === 'base_rate' || key === 'floor_rate') return `${Number.isInteger(value) ? value : RATE.format(value)}%`;
    if (/(^|_)(leverage|ratio)($|_)/.test(key)) return `${RATIO.format(value)}x`;
    if (/(^|_)(days|count)($|_)/.test(key)) return NUMBER.format(value);
    if (MONEY_KEYS.test(key)) return `${currency ? `${currency} ` : ''}${NUMBER.format(value)}`;
    return NUMBER.format(value);
  }
  const text = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? formatDate(text) : codeWords(text);
};

const pointer = (parts: (string | number)[]): string =>
  `/${parts.map(part => String(part).replace(/~/g, '~0').replace(/\//g, '~1')).join('/')}`;

const read = (root: unknown, parts: (string | number)[]): unknown =>
  parts.reduce<unknown>((node, part) => (node == null ? undefined : (node as Record<string, unknown>)[part as string]), root);

const stated = (value: unknown): boolean => value !== null && value !== undefined && value !== '';
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const NO_SOURCE: SourceCitation = {
  page: null, section: null, clause: null, quote: null, status: 'MISSING', scope: 'FIELD', origin: null,
};

/** The best of several citations for one row: a reviewer's first, then a checked one, then any. */
const bestCitation = (review: AgreementReviewRecord, pointers: string[]): SourceCitation => {
  const held = pointers.map(path => review.citations[path]).filter(Boolean);
  return held.find(entry => entry.status === 'CONFIRMED') || held.find(entry => entry.status === 'VERIFIED')
    || held.find(entry => entry.status !== 'MISSING') || NO_SOURCE;
};

const covenantUnit = (covenant: Record<string, unknown>): 'x' | 'money' | 'plain' => {
  const kind = `${covenant.covenant_category || ''} ${covenant.metric_type || ''} ${covenant.covenant_name || ''}`.toUpperCase();
  if (/LEVERAGE|COVERAGE|RATIO/.test(kind)) return 'x';
  return Number(covenant.threshold_value ?? covenant.new_threshold_value) >= 100000 ? 'money' : 'plain';
};

/** A covenant level as a number: 6.5, or the ratio text "6.50:1.00". NaN when it is neither. */
const levelNumber = (value: unknown): number => {
  const ratio = /^\s*(\d+(?:\.\d+)?)\s*(?::\s*1(?:\.0+)?|x)\s*$/i.exec(String(value ?? ''));
  return ratio ? Number(ratio[1]) : Number(value);
};

const covenantLevel = (value: unknown, unit: 'x' | 'money' | 'plain', currency: string | null): string => {
  if (!stated(value)) return 'Not stated';
  const amount = levelNumber(value);
  // Text that is not a number is the document's own wording: show it, never "Not stated".
  if (Number.isNaN(amount)) return String(value);
  if (unit === 'x') return `${RATIO.format(amount)}x`;
  return unit === 'money' ? `${currency ? `${currency} ` : ''}${NUMBER.format(amount)}` : NUMBER.format(amount);
};

const covenantRow = (
  covenant: Record<string, unknown>, index: number, review: AgreementReviewRecord, currency: string | null,
): AgreementRow => {
  const unit = covenantUnit(covenant);
  const direction = String(covenant.covenant_direction || '').toUpperCase();
  const bound = direction === 'MAX' ? 'At most ' : direction === 'MIN' ? 'At least ' : '';
  const steps = Array.isArray(covenant.step_schedule) ? covenant.step_schedule.filter(isRecord) : [];
  const tested = [covenant.measurement_frequency, covenant.measurement_period].filter(stated)
    .map(part => String(part).replace(/_/g, ' ').toLowerCase()).join(', ');
  // A notice or certificate reports the test itself; an agreement only sets the level.
  const actual = covenant.actual_value ?? covenant.current_actual_value;
  const outcome = covenant.passed === true ? 'passed' : covenant.passed === false ? 'failed'
    : stated(covenant.compliance_status) ? codeWords(String(covenant.compliance_status)).toLowerCase() : null;
  const detail = [
    tested ? `Tested ${tested}` : null,
    steps.length > 1
      ? `Steps: ${steps.map(step => `${covenantLevel(step.value, unit, currency)} from ${formatDate(String(step.date))}`).join(' · ')}`
      : null,
    covenant.has_equity_cure === true
      ? `Equity cure allowed${stated(covenant.cure_period_days) ? ` within ${covenant.cure_period_days} days` : ''}${stated(covenant.max_equity_cures) ? `, up to ${covenant.max_equity_cures} times` : ''}`
      : null,
    stated(actual)
      ? `Reported ${covenantLevel(actual, unit, currency)}${stated(covenant.testing_date) ? ` as of ${formatDate(String(covenant.testing_date))}` : ''}${outcome ? ` · ${outcome}` : ''}`
      : null,
  ].filter(Boolean).join('. ');
  const at = (key: string) => pointer(['financial_covenants', index, key]);
  return {
    uid: pointer(['financial_covenants', index]),
    section: 'covenants',
    group: COVENANT_GROUP,
    label: String(covenant.covenant_name || `Covenant ${index + 1}`),
    value: stated(covenant.threshold_value) ? `${bound}${covenantLevel(covenant.threshold_value, unit, currency)}`
      : codeWords(String(covenant.covenant_type || 'Covenant')),
    detail: detail || null,
    extractedValue: null,
    citation: bestCitation(review, [at('covenant_name'), at('threshold_value')]),
    sourcePointer: at('covenant_name'),
  };
};

/** A lender of record: who, in what role, for how much. */
const lenderRow = (
  lender: Record<string, unknown>, index: number, review: AgreementReviewRecord, currency: string | null,
): AgreementRow => {
  const at = (key: string) => pointer(['lender_register', index, key]);
  return {
    uid: pointer(['lender_register', index]),
    section: 'terms',
    group: LENDER_GROUP,
    label: String(lender.lender_name || `Lender ${index + 1}`),
    value: [
      stated(lender.commitment_amount) ? formatValue('commitment_amount', lender.commitment_amount, currency) : null,
      stated(lender.pro_rata_pct) ? formatValue('pro_rata_pct', lender.pro_rata_pct, null) : null,
    ].filter(Boolean).join(' · ') || 'Amount not stated',
    detail: stated(lender.lender_role) ? codeWords(String(lender.lender_role)) : null,
    extractedValue: null,
    citation: bestCitation(review, [at('lender_name'), at('commitment_amount')]),
    sourcePointer: at('lender_name'),
  };
};

/** False when an amendment restates a level without changing it. */
const amendmentChanges = (change: Record<string, unknown>): boolean =>
  !stated(change.old_threshold_value) || levelNumber(change.old_threshold_value) !== levelNumber(change.new_threshold_value);

/** An amendment's covenant change, read as the old level becoming the new one. */
const covenantChangeRow = (
  change: Record<string, unknown>, index: number, review: AgreementReviewRecord, currency: string | null,
): AgreementRow => {
  const unit = covenantUnit(change);
  const at = (key: string) => pointer(['amendment_updates', index, key]);
  const unchanged = !amendmentChanges(change);
  return {
    uid: pointer(['amendment_updates', index]),
    section: 'terms',
    group: 'Amendment',
    label: [change.covenant_name, change.period].filter(stated).join(' · ') || `Change ${index + 1}`,
    value: unchanged ? `${covenantLevel(change.new_threshold_value, unit, currency)} (unchanged)`
      : `${covenantLevel(change.old_threshold_value, unit, currency)} → ${covenantLevel(change.new_threshold_value, unit, currency)}`,
    detail: unchanged ? 'Restated by the amendment, not changed' : 'Before the amendment → after it',
    extractedValue: null,
    citation: bestCitation(review, [at('new_threshold_value'), at('covenant_name'), at('old_threshold_value')]),
    sourcePointer: at('new_threshold_value'),
  };
};

/** The document as the reviewer reads it: grouped terms, then covenants, each with its source. */
export const buildAgreementGroups = (review: AgreementReviewRecord): AgreementGroup[] => {
  const record = review.record || {};
  const currency = (read(record, ['loan_terms', 'currency']) as string | null) || null;
  // A syndicated deal's size is the borrower's facility, not the fund's commitment.
  const syndicated = assetLane(review.document_type) === 'LEVERAGED_LOAN';
  const groups = new Map<string, AgreementGroup>();
  const groupFor = (name: string): AgreementGroup => {
    const shown = !syndicated ? name : name === TERMS_GROUP ? 'Size and dates' : name === 'Facility' ? 'Deal' : name;
    if (!groups.has(shown)) groups.set(shown, { name: shown, rows: [], notStated: [] });
    return groups.get(shown) as AgreementGroup;
  };

  const addTerm = (group: AgreementGroup, parts: (string | number)[], label: string, value: unknown, money: string | null) => {
    const key = String(parts[parts.length - 1]);
    if (!stated(value)) {
      if (!ONLY_WHEN_STATED.test(key)) group.notStated.push(label);
      return;
    }
    const uid = pointer(parts);
    const first = review.extracted_record ? read(review.extracted_record, parts) : undefined;
    const corrected = review.extracted_record != null && stated(first) && JSON.stringify(first) !== JSON.stringify(value);
    group.rows.push({
      uid, section: 'terms', group: group.name, label,
      value: formatValue(key, value, money),
      detail: null,
      extractedValue: corrected ? formatValue(key, first, money) : null,
      citation: review.citations[uid] || NO_SOURCE,
      sourcePointer: uid,
    });
  };

  const walk = (group: AgreementGroup, parts: (string | number)[], value: unknown, prefix: string, money: string | null) => {
    const key = String(parts[parts.length - 1]);
    if (NOT_TERMS.has(key) || key.startsWith('_')) return;
    if (Array.isArray(value)) {
      const name = RECORD_NAMES[key] || labelOf(key);
      value.forEach((entry, index) => {
        if (key === 'amendment_updates' && isRecord(entry) && stated(entry.new_threshold_value)) {
          group.rows.push(covenantChangeRow(entry, index, review, money));
        } else if (key === 'lender_register' && isRecord(entry)) {
          group.rows.push(lenderRow(entry, index, review, money));
        } else walk(group, [...parts, index], entry, `${prefix}${name} ${index + 1} · `, money);
      });
    } else if (isRecord(value)) {
      // A spread that changes is one line, old → new, not two unrelated numbers.
      const changed = key === 'amendment_pricing' && stated(value.old_spread_bps) && stated(value.new_spread_bps);
      if (changed) {
        const uid = pointer([...parts, 'new_spread_bps']);
        group.rows.push({
          uid, section: 'terms', group: group.name, label: 'Spread',
          value: `${formatValue('spread_bps', value.old_spread_bps, null)} → ${formatValue('spread_bps', value.new_spread_bps, null)}`,
          detail: 'Before the amendment → after it', extractedValue: null,
          citation: bestCitation(review, [uid, pointer([...parts, 'old_spread_bps'])]), sourcePointer: uid,
        });
      }
      Object.entries(value).sort(([left], [right]) => rank(left) - rank(right))
        .filter(([childKey]) => !(changed && ['old_spread_bps', 'new_spread_bps'].includes(childKey)))
        .forEach(([childKey, child]) => walk(group, [...parts, childKey], child, prefix, money));
    } else {
      const label = key === 'base_rate' && typeof value === 'number' ? 'Index rate'
        : syndicated && key === 'original_principal' ? 'Facility size' : labelOf(key);
      addTerm(group, parts, typeof parts[parts.length - 1] === 'number' ? prefix.replace(/ · $/, '') : `${prefix}${label}`, value, money);
    }
  };

  const placed = new Set<string>(['financial_covenants', 'facilities']);
  GROUPS.forEach(({ name, keys }) => keys.forEach(key => {
    placed.add(key);
    if (key in record) walk(groupFor(name), [key], record[key], '', currency);
  }));
  // A syndicated deal has several tranches, each with its own identifiers, pricing and maturity:
  // one heading per tranche, never one blended "facility".
  const tranches = Array.isArray(record.facilities) ? record.facilities : [];
  tranches.forEach((tranche, index) => {
    if (!isRecord(tranche)) return;
    const name = tranche.facility_name || tranche.tranche_code || tranche.facility_type || `Tranche ${index + 1}`;
    walk(groupFor(`Tranche · ${codeWords(String(name))}`), ['facilities', index], tranche, '', (tranche.currency as string) || currency);
  });
  // A block the page does not know by name is still part of the document: it gets its own heading.
  Object.keys(record).filter(key => !placed.has(key) && !NOT_TERMS.has(key)).forEach(key =>
    walk(groupFor(isRecord(record[key]) || Array.isArray(record[key]) ? sentence(key) : OTHER_GROUP), [key], record[key], '', currency));

  const covenants = Array.isArray(record.financial_covenants) ? record.financial_covenants.filter(isRecord) : [];
  if (covenants.length) {
    groupFor(COVENANT_GROUP).rows.push(...covenants.map((covenant, index) => covenantRow(covenant, index, review, currency)));
  }

  const built = Array.from(groups.values()).filter(group => group.rows.length > 0);
  const kind = documentKind(review.document_type);
  const pick = (test: (group: AgreementGroup) => boolean) => [...built.filter(test), ...built.filter(group => !test(group))];
  if (kind === 'AGREEMENT') return pick(group => !EVENT_GROUPS.has(group.name));

  // A compliance certificate is about the covenant tests, so they lead.
  if (kind === 'CERTIFICATE') return pick(group => group.name === COVENANT_GROUP);
  return built;
};

/** A source the reviewer no longer has to chase: checked against the page, or confirmed by a person. */
export const sourceSettled = (citation: SourceCitation): boolean =>
  citation.status === 'VERIFIED' || citation.status === 'CONFIRMED';

/** How a citation reads to the reviewer: an icon and a word, never colour alone. */
export const sourceChip = (citation: SourceCitation): { tone: StatusTone; label: string } => {
  if (citation.status === 'CONFIRMED') return { tone: 'done', label: 'Confirmed' };
  if (citation.status === 'VERIFIED') return { tone: 'done', label: 'Source confirmed' };
  if (citation.status === 'UNVERIFIED') return { tone: 'warning', label: 'Check source' };
  return { tone: 'neutral', label: 'No source' };
};

export const sourceNote = (citation: SourceCitation): string => {
  if (citation.status === 'CONFIRMED') {
    return `Source confirmed by ${citation.reviewed_by || 'a reviewer'}${citation.reviewed_at ? `, ${formatStamp(citation.reviewed_at)}` : ''}.`;
  }
  if (citation.status === 'VERIFIED') {
    return citation.scope === 'RECORD'
      ? `This passage sets out the whole record this value belongs to. Its wording was found on page ${citation.page} of the document.`
      : `The quoted wording was found on page ${citation.page} of the document.`;
  }
  if (citation.status === 'UNVERIFIED') {
    return 'The quoted wording was not found word for word in the document text. Open the page and check it.';
  }
  return 'Open the document to check this value against it.';
};

export const sourceSummary = (citation: SourceCitation): string => {
  if (citation.status === 'MISSING') return '—';
  return [citation.page ? `p. ${citation.page}` : null, citation.clause || citation.section].filter(Boolean).join(' · ') || '—';
};

/** The row in the shape the shared evidence panel reads. */
export const toEvidenceRow = (row: AgreementRow): ReviewRow => ({
  uid: row.uid,
  kind: row.section === 'covenants' ? 'COVENANT' : 'TERM',
  section: row.section,
  id: null,
  key: row.uid,
  label: row.label,
  group: row.group,
  value: row.value,
  detail: row.detail,
  extractedValue: row.extractedValue,
  corrected: row.extractedValue != null,
  citation: row.citation.status === 'MISSING' ? null : {
    page: row.citation.page != null ? String(row.citation.page) : null,
    section: row.citation.section,
    clause: row.citation.clause,
    excerpt: row.citation.quote,
  },
  entered: null,
  confidence: null,
  status: 'INFO',
  problem: null,
  message: null,
  owner: null,
  actions: [],
  valueType: null,
  required: false,
  material: true,
});

export function formatStamp(raw: string | null | undefined): string {
  if (!raw) return '';
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? raw
    : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date);
}

export const readText = (review: AgreementReviewRecord, parts: (string | number)[]): string | null => {
  const value = read(review.record, parts);
  return stated(value) ? String(value) : null;
};

const firstDate = (review: AgreementReviewRecord, paths: string[][]): string => {
  const value = paths.map(parts => readText(review, parts)).find(Boolean);
  return value ? formatDate(value) : 'Not stated';
};

const currencyOf = (review: AgreementReviewRecord): string | null => readText(review, ['loan_terms', 'currency']);

const pricing = (review: AgreementReviewRecord): string => {
  const rate = (read(review.record, ['interest_rate']) || {}) as Record<string, unknown>;
  const parts = [
    stated(rate.base_type) ? formatValue('base_type', rate.base_type, null) : null,
    stated(rate.spread_bps) ? `+ ${formatValue('spread_bps', rate.spread_bps, null)}` : null,
  ].filter(Boolean).join(' ');
  return parts ? `${parts}${stated(rate.floor_pct) ? ` · ${formatValue('floor_pct', rate.floor_pct, null)} floor` : ''}` : 'Not stated';
};

/** The three facts the page leads with, chosen for the kind of document. The page adds the source count. */
export const summaryFacts = (review: AgreementReviewRecord): { label: string; value: string }[] => {
  const record = review.record || {};
  const kind = documentKind(review.document_type);
  const covenants = Array.isArray(record.financial_covenants) ? record.financial_covenants.filter(isRecord) : [];
  if (kind === 'AGREEMENT' && assetLane(review.document_type) === 'LEVERAGED_LOAN') {
    const tranches = (Array.isArray(record.facilities) ? record.facilities : []).filter(isRecord);
    const sizes = tranches.map(tranche => Number(tranche.original_size));
    const sized = tranches.length > 0 && sizes.every(size => Number.isFinite(size) && size > 0);
    return [
      { label: 'Deal', value: readText(review, ['deal_name']) || readText(review, ['deal_code']) || 'Not stated' },
      { label: 'Tranches', value: tranches.length ? `${tranches.length}${sized ? ` · ${currencyOf(review) || ''} ${NUMBER.format(sizes.reduce((sum, size) => sum + size, 0))} in total`.replace('  ', ' ') : ''}` : 'None stated' },
      { label: 'Covenants', value: covenants.length ? `${covenants.length}` : record.cov_lite_acknowledged === true ? 'Covenant-lite' : 'None stated' },
    ];
  }
  if (kind === 'AGREEMENT') {
    const amount = read(record, ['loan_terms', 'original_principal']);
    return [
      { label: 'Commitment', value: stated(amount) ? formatValue('original_principal', amount, currencyOf(review)) : 'Not stated' },
      { label: 'Pricing', value: pricing(review) },
      { label: 'Maturity', value: firstDate(review, [['loan_terms', 'maturity_date']]) },
    ];
  }
  if (kind === 'TRADE_CONFIRM') {
    const trade = (read(record, ['trade_settlement_details']) || read(record, ['trade_confirmation']) || {}) as Record<string, unknown>;
    return [
      { label: 'Par amount', value: stated(trade.par_amount) ? formatValue('par_amount', trade.par_amount, currencyOf(review)) : 'Not stated' },
      { label: 'Price', value: stated(trade.transfer_price) ? formatValue('transfer_price', trade.transfer_price, null) : 'Not stated' },
      { label: 'Settlement date', value: stated(trade.settlement_date) ? formatDate(String(trade.settlement_date)) : 'Not stated' },
    ];
  }
  const effective = firstDate(review, [['notice_event', 'effective_date'], ['key_dates', 'notice_effective_date'],
    ['amendment_pricing', 'pricing_effective_date'], ['key_dates', 'document_date']]);
  if (kind === 'AMENDMENT') {
    const updates = (Array.isArray(record.amendment_updates) ? record.amendment_updates : [])
      .filter(entry => isRecord(entry) && amendmentChanges(entry)).length;
    const changes = updates + (stated(read(record, ['amendment_pricing', 'new_spread_bps'])) ? 1 : 0);
    return [
      { label: 'Amendment', value: readText(review, ['amendment_name']) || 'Amendment' },
      { label: 'Effective', value: effective },
      { label: 'Changes', value: `${changes} term${changes === 1 ? '' : 's'} changed` },
    ];
  }
  if (kind === 'CERTIFICATE') {
    const failed = covenants.filter(c => c.passed === false || /BREACH|FAIL|NON_?COMPLIANT/.test(String(c.compliance_status || '').toUpperCase())).length;
    const reported = covenants.filter(c => stated(c.actual_value ?? c.current_actual_value)).length;
    return [
      { label: 'Period end', value: firstDate(review, [['key_dates', 'compliance_period_end'], ['key_dates', 'covenant_testing_date'], ['key_dates', 'document_date']]) },
      { label: 'Covenants reported', value: `${reported} of ${covenants.length}` },
      { label: 'Result as reported', value: reported === 0 ? 'No results stated' : failed ? `${failed} not met` : 'All met' },
    ];
  }
  const amount = read(record, ['notice_event', 'event_amount']);
  const interest = read(record, ['interest_payment_inputs', 'stated_interest_amount']);
  return [
    { label: 'Event', value: codeWords(readText(review, ['notice_event', 'event_type']) || 'Not stated') },
    stated(amount) || !stated(interest)
      ? { label: 'Amount', value: stated(amount) ? formatValue('event_amount', amount, currencyOf(review)) : 'Not stated' }
      : { label: 'Interest stated', value: formatValue('stated_interest_amount', interest, currencyOf(review)) },
    { label: 'Effective', value: effective },
  ];
};
